/* eslint-disable max-lines */
"use client";
import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import Typography from "../../../atoms/typography";
import { constants } from "../../../../constants/pageConstants/pdpConstants";
import isEmpty from "lodash.isempty";
import {
  requestToFetchShippingAndTimeSlotAction,
  resetShippingLoader,
} from "../../../../store/slices/common/shippingDateAndSlotSelection";
import TimeSlots from "../../../molecules/timeSlots/timeSlots";
import useAnalytics from "../../../../analytics/useAnalytics";
import { setGaShippingMethodName, setGaTimeSlotSelected } from "../../../../analytics/gaEvents";
import { GA4_EVENTS } from "../../../../analytics/gaConstant";
import Loader from "../../../atoms/loader";
import { getProductDeliveryType, updateCurrency } from "../../../../utils/common";
import { cleverTapCommonEvents, cleverTapCommonWords } from "../../../../analytics/clevertapConstants";
import {
  calenderIsTodayOrTomorrowDate,
  getShippingMethodsInStringAndCount,
} from "../../../../utils/common/deliveryDate";

/**
 *
 * @param {object} currentItem
 * @param {function} onTimeAndShippingMethodSelection
 * @param {boolean} isMobile
 * @param {string} selectedDate
 * @param {boolean} hasTimeSlots
 * @param {string} selectedCurrency
 * @param {function} setUserSelectedTimeSlotId
 * @param {object} currentShippingMethod
 * @param {function} setCurrentShippingMethod
 * @param {function} setHasTimeSlot
 * @param {function} setHasDateSelector
 * @param {boolean} isCart
 * @param {function} setIsSameShippingSelected
 * @param {object} cartCleverTapData
 * @param {object} popupCleverTapData
 * @param {boolean} isCourier
 * @returns The UI displaying shipping methods and time slots for domestic delivery.
 */
const DeliveryShippingAndTimeSlotSelection = ({
  currentItem = {},
  onTimeAndShippingMethodSelection,
  isMobile,
  selectedDate = "",
  hasTimeSlots,
  selectedCurrency,
  setUserSelectedTimeSlotId,
  currentShippingMethod,
  setCurrentShippingMethod,
  setHasTimeSlot,
  setHasDateSelector,
  isCart,
  setIsSameShippingSelected,
  cartCleverTapData,
  popupCleverTapData,
}) => {
  const dispatch = useDispatch();
  const { trackEvent } = useAnalytics();
  const updatedCleverTapData = useRef(null);
  const { selectedDomesticLocation } = useSelector((state) => state?.locationLock);

  const { shippingAndTimeSlot, selectedDeliveryDetails, loading, isShippingLoaded, isCartVisible } = useSelector(
    (state) => ({
      shippingAndTimeSlot: state?.shippingDateAndSlotSelection?.shippingAndTimeSlot,
      selectedDeliveryDetails: state?.shippingDateAndSlotSelection?.selectedDeliveryDetails,
      loading: state?.shippingDateAndSlotSelection?.loading,
      isShippingLoaded: state?.shippingDateAndSlotSelection?.isShippingLoaded,
      isCartVisible: state?.cart?.isCartVisible,
    }),
  );

  const address = selectedDomesticLocation?.address;
  const pinCode = selectedDomesticLocation?.pincode;
  const [shippingDataList, setShippingDataList] = useState([]);

  useEffect(() => {
    if (!isEmpty(pinCode)) {
      const queryParams = {
        productId: currentItem?.productId,
        pinCode: isCart ? currentItem?.pincode : pinCode,
        deliveryDate: selectedDate.replace(/-/g, "/"),
        countryGeoId: "IND",
      };
      dispatch(requestToFetchShippingAndTimeSlotAction(queryParams));
    }
  }, [pinCode, selectedDate]);

  /**
   * Handles time slot selection and dispatches necessary actions.
   *
   * @param {Object} timeSlots - The selected time slot object.
   */
  const onHandleTimeSlotSelection = (e, timeSlots) => {
    dispatch(resetShippingLoader());
    trackEvent(
      GA4_EVENTS?.TIME_SLOT_SELECTED,
      { ...setGaTimeSlotSelected(timeSlots?.timeSlots?.startTime, timeSlots?.timeSlots?.endTime) },
      ["ga4"],
    );
    onTimeAndShippingMethodSelection({ currentShippingMethod, timeSlots });
    setHasTimeSlot(false);
    setHasDateSelector(false);
  };

  /**
   * Handles click on shipping method selection.
   *
   * @param {string} shippingMethodId - The ID of the selected shipping method.
   * @param {string} shippingMethodName - The name of the selected shipping method.
   * @param {string} [timeSlotId] - The ID of the selected time slot (optional).
   */
  const onHandleShippingSelection = (shippingMethodId, shippingMethodName, priceDetails) => {
    trackEvent(GA4_EVENTS?.SHIPPING_METHOD_SELECTED, { ...setGaShippingMethodName(shippingMethodName) }, ["ga4"]);
    setCurrentShippingMethod({ shippingMethodId: shippingMethodId, shippingMethodName: shippingMethodName });
    if (isCart) {
      setIsSameShippingSelected(shippingMethodId === currentItem?.shippingAndTimeSlot?.data?.shippingMethodId);
      updatedCleverTapData.current = {
        ...cartCleverTapData,
        delivery_shipping: shippingMethodName || "",
        delivery_slot: "",
        date_of_delivery: selectedDate,
        delivery_shipping_fee: updateCurrency(selectedCurrency, priceDetails?.price, false, false, false),
      };
    } else {
      updatedCleverTapData.current = {
        ...popupCleverTapData,
        delivery_shipping: shippingMethodName || "",
        delivery_slot: "",
        date_of_delivery: selectedDate,
        delivery_shipping_fee: updateCurrency(selectedCurrency, priceDetails?.price, false, false, false),
      };
    }
    trackEvent(cleverTapCommonEvents.deliveryShippingSelected, updatedCleverTapData.current, ["clevertap"]);
  };

  /**
   *
   * @param {*} param0
   * @returns
   */
  const getCurrentShippingMethod = ({ shipping }) => {
    const currentItemDate = isCartVisible
      ? currentItem?.shippingDetails?.deliveryDate.fullDeliveryDate
      : currentItem?.shippingAndTimeSlot?.data?.deliveryDate?.fullDeliveryDate;

    if (isCartVisible && selectedDate === currentItemDate) {
      return {
        shippingMethodId: currentItem?.shippingDetails?.shippingMethodId,
        shippingMethodName: currentItem?.shippingDetails?.shippingMethodName,
      };
    } else if (!isCartVisible && selectedDate === currentItemDate) {
      return {
        shippingMethodId: currentItem?.shippingAndTimeSlot?.data?.shippingMethodId,
        shippingMethodName: currentItem?.shippingAndTimeSlot?.data?.shippingMethodName,
      };
    } else if (selectedDate === selectedDeliveryDetails?.selectedDate) {
      return selectedDeliveryDetails?.currentShippingMethod;
    } else {
      return shipping?.[0];
    }
  };

  useEffect(() => {
    if (!isEmpty(shippingAndTimeSlot)) {
      const shipping = shippingAndTimeSlot?.data?.map((shipping) => {
        const shippingMethodId = Object.keys(shipping)[0];
        return {
          shippingMethodId,
          shippingMethodName: shipping[shippingMethodId]?.shippingMethodName,
        };
      });
      setCurrentShippingMethod(getCurrentShippingMethod({ shipping }));
      setShippingDataList(shippingAndTimeSlot?.data?.map((item) => Object.values(item)[0]));
    }
  }, [shippingAndTimeSlot]);

  useEffect(() => {
    if (!isEmpty(shippingAndTimeSlot) && isShippingLoaded) {
      const { methodsString, methodsCount } = getShippingMethodsInStringAndCount(shippingAndTimeSlot?.data);
      const productDeliveryType = getProductDeliveryType(currentItem?.primaryCategoryId);
      const urlPath = currentItem?.urlIdentifier;
      const cleverTapDataShippingViewed = {
        product_name: currentItem?.productName,
        pid: currentItem?.productId,
        product_category: currentItem?.primaryCategoryId || currentItem?.primaryProductCategoryId,
        product_price: currentItem?.productPrice?.price || currentItem?.mainProduct?.price,
        delivery_type: productDeliveryType,
        pincode: pinCode,
        area: address,
        system_pincode: pinCode,
        country: currentItem?.catalogId || currentItem?.productCatalogId,
        city: "",
        is_international: false,
        available_delivery_shipping_methods: methodsString,
        available_delivery_shipping_count: methodsCount,
        date_of_delivery: selectedDate || "",
        is_date_today: calenderIsTodayOrTomorrowDate(selectedDate)?.isDateToday,
        is_date_tomorrow: calenderIsTodayOrTomorrowDate(selectedDate)?.isDateTomorrow,
      };

      if (isCartVisible) {
        const updateCleverTapDataShippingViewed = {
          ...cleverTapDataShippingViewed,
          page_name: cleverTapCommonWords.CART,
          page_type: cleverTapCommonWords.CART,
        };
        trackEvent(cleverTapCommonEvents.deliveryShippingViewed, updateCleverTapDataShippingViewed, ["clevertap"]);
      } else {
        const updateCleverTapDataShippingViewed = {
          ...cleverTapDataShippingViewed,
          page_name: urlPath,
          page_type: cleverTapCommonWords.PDP,
        };
        trackEvent(cleverTapCommonEvents.deliveryShippingViewed, updateCleverTapDataShippingViewed, ["clevertap"]);
      }
    }
  }, [isShippingLoaded]);

  /**
   *
   */
  const renderShippingMethodCard = (method, methodDetails) => {
    const activeMethodClasses =
      "relative bg-fnp-50 before:absolute before:top-1/4 before:h-1/2 before:w-4 before:bg-fnp-600 before:rounded-r-7 ";

    return (
      <div
        key={method}
        className={`flex flex-col justify-start gap-4 py-10 hover:cursor-pointer ${
          currentShippingMethod?.shippingMethodName === methodDetails?.shippingMethodName ? activeMethodClasses : ""
        }`}
        onClick={() =>
          onHandleShippingSelection(method, methodDetails?.shippingMethodName, methodDetails?.priceDetails)
        }
      >
        <Typography
          variant="p"
          Tag="p"
          classes={`${currentShippingMethod?.shippingMethodName === methodDetails?.shippingMethodName && "font-600"} md:text-14 font-400 leading-5 text-fnp-500 pl-16 ${isMobile && "pr-10"}`}
        >
          {methodDetails?.shippingMethodName}
        </Typography>
        <Typography variant="p" Tag="p" classes="text-12 md:text-14 font-400 leading-5 text-fnp-500 pl-16">
          {`${selectedCurrency?.currency?.currencyCode} ${methodDetails?.priceDetails?.price}`}
        </Typography>
      </div>
    );
  };
  /**
   * Returns the appropriate shipping method card based on the international flag and time slots visibility.
   *
   * @param {boolean} hasTimeSlots - Flag to check if time slots should be displayed.
   * @param {Object} currentShippingMethod - The currently selected shipping method.
   * @param {Object} shippingAndTimeSlot - The details of the available shipping methods.
   * @returns {React.ReactNode} The rendered shipping method card(s).
   */
  const getShippingMethodCards = () => {
    return shippingAndTimeSlot?.data?.map((shipping) => {
      const shippingMethodId = Object.keys(shipping)[0];
      return renderShippingMethodCard(shippingMethodId, shipping[shippingMethodId]);
    });
  };

  return (
    <div className="w-full">
      {loading ? (
        <Loader />
      ) : (
        <>
          {shippingAndTimeSlot && hasTimeSlots && (
            <div className={`flex w-full flex-col ${isCart ? "px-5" : ""}`} id="time-slot-selection-section">
              <Typography variant="p" Tag="p" classes="text-16 md:text-14 lg:text-16 font-600 pb-12 leading-18">
                {constants?.SELECT_TIME_SLOT}
              </Typography>
              <div className="flex w-full gap-16 overflow-hidden rounded border border-thinLightGrey">
                <div className="flex w-[45%] flex-col gap-12 border border-thinLightGrey">
                  {getShippingMethodCards()}
                </div>

                <div className="flex flex-col gap-10 py-10">
                  <Typography variant="p" Tag="p" classes="text-14 font-400 leading-5 text-fnp-500">
                    {constants?.SELECT_AN_OPTION.toUpperCase()}
                  </Typography>
                  {shippingDataList
                    ?.filter(
                      (shippingData) => shippingData?.shippingMethodName === currentShippingMethod?.shippingMethodName,
                    )
                    .map((shippingData, index) => (
                      <div className="flex flex-col justify-start py-10" key={index}>
                        <TimeSlots
                          timeSlots={shippingData.timeSlots}
                          onTimeSlotSelection={onHandleTimeSlotSelection}
                          popupCleverTapData={updatedCleverTapData.current}
                          cartCleverTapData={updatedCleverTapData.current}
                          setUserSelectedTimeSlotId={setUserSelectedTimeSlotId}
                          currentItem={currentItem}
                          isCart={isCart}
                          selectedCurrency={selectedCurrency}
                          price={shippingData?.priceDetails?.price}
                          shippingMethodName={shippingData?.shippingMethodName}
                          selectedDate={selectedDate}
                          isPDP={true}
                          currentShippingMethod={currentShippingMethod}
                        />
                      </div>
                    ))}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

DeliveryShippingAndTimeSlotSelection.propTypes = {
  isCart: PropTypes.bool,
  isMobile: PropTypes.bool,
  isCourier: PropTypes.bool,
  hasTimeSlots: PropTypes.bool,
  currentItem: PropTypes.object,
  setHasTimeSlot: PropTypes.func,
  selectedDate: PropTypes.string,
  setHasDateSelector: PropTypes.func,
  selectedCurrency: PropTypes.object,
  cartCleverTapData: PropTypes.object,
  popupCleverTapData: PropTypes.object,
  currentShippingMethod: PropTypes.object,
  setCurrentShippingMethod: PropTypes.func,
  setUserSelectedTimeSlotId: PropTypes.func,
  setIsSameShippingSelected: PropTypes.func,
  onTimeAndShippingMethodSelection: PropTypes.func,
};

export default DeliveryShippingAndTimeSlotSelection;
